'use client'

import React, { ReactElement } from 'react'

import { TimelineEventProps } from './TimelineEvent'
import { EmptyState, Spinner } from '../..'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    /** <Button> that is shown at the top of the list, i.e. "Add note" */
    action?: React.ReactElement
    /** <EmptyState> component shown when there are no "children" */
    emptyState: React.ReactElement
    /** Is the timeline loading? */
    isLoading?: boolean
    /** List of <TimelineEvent> components or custom wrappers of the <TimelineEvent> component.
     * IMPORTANT NOTE: Each child gets the props "isFirst" and "isLast" added so in case of wrapper components, make sure to forward them to the TimelineEvent component */
    children: React.ReactNode
}

export const Timeline = ({ action, emptyState, isLoading, children }: Props): ReactElement => {
    const mappedChildren = React.Children.toArray(children)
        .filter((child) => !!child)
        .map((element, index, array) => {
            if (React.isValidElement(element)) {
                return React.cloneElement(element as React.ReactElement<TimelineEventProps>, {
                    isFirst: index === 0,
                    isLast: index === array.length - 1,
                })
            }
            return element
        })

    return (
        <div className="flex flex-col gap-2" data-testid="timeline">
            {action && action}
            {isLoading && (
                <EmptyState hideIcon direction="vertical">
                    <Spinner />
                </EmptyState>
            )}
            {!isLoading && !mappedChildren.length && emptyState}
            {!isLoading && !!mappedChildren.length && <ol className="list-none flex flex-col gap-0">{mappedChildren}</ol>}
        </div>
    )
}
